'use client';

import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { JsonLd } from './components/JsonLd';
import { StickyButtons } from './components/StickyButtons';
import { useState, Suspense } from 'react';
import dynamic from 'next/dynamic';

// Dynamic imports for better performance - load below-the-fold content lazily
const AboutSection = dynamic(() => import('./components/AboutSection').then(mod => ({ default: mod.AboutSection })), { 
  ssr: true,
  loading: () => <div className="h-96 bg-white animate-pulse" />
});

const GallerySection = dynamic(() => import('./components/GallerySection').then(mod => ({ default: mod.GallerySection })), { 
  ssr: true,
  loading: () => <div className="h-96 bg-white animate-pulse" />
});

const AmenitiesSection = dynamic(() => import('./components/AmenitiesSection').then(mod => ({ default: mod.AmenitiesSection })), { 
  ssr: true,
  loading: () => <div className="h-96 bg-black animate-pulse" />
});

const FloorPlanSection = dynamic(() => import('./components/FloorPlanSection').then(mod => ({ default: mod.FloorPlanSection })), { 
  ssr: true,
  loading: () => <div className="h-96 bg-black animate-pulse" />
});

const PaymentPlanSection = dynamic(() => import('./components/PaymentPlanSection').then(mod => ({ default: mod.PaymentPlanSection })), { 
  ssr: true,
  loading: () => <div className="h-96 bg-white animate-pulse" />
});

const LocationSection = dynamic(() => import('./components/LocationSection').then(mod => ({ default: mod.LocationSection })), { 
  ssr: true,
  loading: () => <div className="h-96 bg-black animate-pulse" />
});

const ContactSection = dynamic(() => import('./components/ContactSection').then(mod => ({ default: mod.ContactSection })), { 
  ssr: true,
  loading: () => <div className="h-96 bg-white animate-pulse" />
});

const Footer = dynamic(() => import('./components/Footer').then(mod => ({ default: mod.Footer })), { 
  ssr: true,
  loading: () => <div className="h-64 bg-black animate-pulse" />
});

const ThankYouModal = dynamic(() => import('./components/ThankYouModal').then(mod => ({ default: mod.ThankYouModal })), { 
  ssr: false
});

export default function Page() {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // ==================== SEO STRUCTURED DATA ====================
  // 🔧 CHANGE THESE VALUES TO CUSTOMIZE YOUR STRUCTURED DATA
  
  // Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: 'Amwaj Development',
    description: 'Amwaj Development is a premier real estate developer in Dubai, UAE, delivering luxury residential communities across the city.',
    url: 'https://gateeleven.ae',
    logo: 'https://gateeleven.ae/logo.png',
    image: 'https://gateeleven.ae/images/gallery/og-image.jpg',
    telephone: '+971 58 898 0456',
    email: 'info@zedcapital.ae',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'District 11, Meydan',
      addressLocality: 'Dubai',
      addressRegion: 'Dubai',
      postalCode: '00000',
      addressCountry: 'AE',
    },
    sameAs: [
      'https://www.facebook.com/yourpage',
      'https://www.instagram.com/yourpage',
      'https://www.linkedin.com/company/yourpage',
      'https://twitter.com/yourpage',
    ],
  };

  // Apartment Complex Schema
  const apartmentSchema = {
    '@context': 'https://schema.org',
    '@type': 'ApartmentComplex',
    name: 'Gate Eleven Dubai by Amwaj Development',
    description: 'Gate Eleven Dubai is a luxury residential development by Amwaj Development featuring 1 & 2-bedroom apartments in District 11, Meydan. Residents enjoy serene lagoon views, world-class amenities, and a refined lifestyle in the heart of Dubai.',
    url: 'https://gateeleven.ae',
    image: 'https://gateeleven.ae/images/gallery/og-image.jpg',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Dubai',
      addressRegion: 'Dubai',
      addressCountry: 'AE',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 25.2048, // Add actual coordinates
      longitude: 55.2708,
    },
    amenityFeature: [
      {
        '@type': 'LocationFeatureSpecification',
        name: 'Swimming Pool',
        value: true,
      },
      {
        '@type': 'LocationFeatureSpecification',
        name: 'Fitness Center',
        value: true,
      },
      {
        '@type': 'LocationFeatureSpecification',
        name: 'Parking',
        value: true,
      },
      {
        '@type': 'LocationFeatureSpecification',
        name: 'Security',
        value: '24/7',
      },
    ],
    numberOfBedrooms: '1-2',
    petsAllowed: false, // Change as needed
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Gate Eleven Dubai',
        item: 'https://gateeleven.ae',
      },
    ],
  };

  return (
    <>
      {/* Structured Data */}
      <JsonLd data={organizationSchema} />
      <JsonLd data={apartmentSchema} />
      <JsonLd data={breadcrumbSchema} />
      
      <div className="min-h-screen bg-white overflow-x-hidden">
        {/* Critical above-the-fold content loaded immediately */}
        <Header />
        <StickyButtons />
        <Hero />
        
        {/* Below-the-fold content wrapped in Suspense for better loading */}
        <Suspense fallback={<div className="h-96 bg-white animate-pulse" />}>
          <AboutSection />
        </Suspense>
        
        <Suspense fallback={<div className="h-96 bg-black animate-pulse" />}>
          <AmenitiesSection />
        </Suspense>

        <Suspense fallback={<div className="h-96 bg-white animate-pulse" />}>
          <GallerySection />
        </Suspense>

        <Suspense fallback={<div className="h-96 bg-[#c19964] animate-pulse" />}>
          <PaymentPlanSection />
        </Suspense>

        <Suspense fallback={<div className="h-96 bg-black animate-pulse" />}>
          <FloorPlanSection />
        </Suspense>

        <Suspense fallback={<div className="h-96 bg-black animate-pulse" />}>
          <LocationSection />
        </Suspense>
        
        <Suspense fallback={<div className="h-96 bg-white animate-pulse" />}>
          <ContactSection />
        </Suspense>
        
        <Suspense fallback={<div className="h-64 bg-black animate-pulse" />}>
          <Footer />
        </Suspense>
      </div>

      {/* Preview Modal - Only load when needed */}
      {showPreviewModal && (
        <Suspense fallback={<div />}>
          <ThankYouModal isOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} />
        </Suspense>
      )}
    </>
  );
}
